import { Vector2 } from 'three';

type KDTreeNode = {
  vertex: Vector2;
  left: KDTreeNode | null;
  right: KDTreeNode | null;
};

const axes = ['x', 'y'] as const;

function getAxis(depth: number) {
  return axes[depth % axes.length];
}

export class Vector2KDTree {
  root: KDTreeNode | null;

  constructor(points: Vector2[], depth = 0) {
    this.root = this.buildTree(points, depth);
  }

  private buildTree(points: Vector2[], depth: number): KDTreeNode | null {
    if (points.length === 0) {
      return null;
    }

    const axis = getAxis(depth);
    const sorted = [...points].sort((a, b) => a[axis] - b[axis]);
    const median = Math.floor(sorted.length / 2);

    const node: KDTreeNode = {
      vertex: sorted[median],
      left: this.buildTree(sorted.slice(0, median), depth + 1),
      right: this.buildTree(sorted.slice(median + 1), depth + 1),
    };

    return node;
  }

  rangeSearchSquare(
    point: Vector2,
    axisDistance: number,
    depth = 0,
    node: KDTreeNode | null = this.root,
    results: Vector2[] = [],
    minX = point.x - axisDistance,
    maxX = point.x + axisDistance,
    minY = point.y - axisDistance,
    maxY = point.y + axisDistance,
  ): Vector2[] {
    if (node === null) {
      return results;
    }

    if (
      node.vertex.x >= minX &&
      node.vertex.x <= maxX &&
      node.vertex.y >= minY &&
      node.vertex.y <= maxY
    ) {
      results.push(node.vertex);
    }

    const axis = getAxis(depth);
    const nodeAxisValue = node.vertex[axis];

    if (axis === 'x') {
      if (minX <= nodeAxisValue) {
        this.rangeSearchSquare(
          point,
          axisDistance,
          depth + 1,
          node.left,
          results,
          minX,
          maxX,
          minY,
          maxY,
        );
      }
      if (maxX >= nodeAxisValue) {
        this.rangeSearchSquare(
          point,
          axisDistance,
          depth + 1,
          node.right,
          results,
          minX,
          maxX,
          minY,
          maxY,
        );
      }
    } else {
      if (minY <= nodeAxisValue) {
        this.rangeSearchSquare(
          point,
          axisDistance,
          depth + 1,
          node.left,
          results,
          minX,
          maxX,
          minY,
          maxY,
        );
      }
      if (maxY >= nodeAxisValue) {
        this.rangeSearchSquare(
          point,
          axisDistance,
          depth + 1,
          node.right,
          results,
          minX,
          maxX,
          minY,
          maxY,
        );
      }
    }

    return results;
  }
}
